import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import CardList from "./ServiceCardStack";
import { SERVICES_SECTION } from "../constants/config";
import { floatingOrb, floatingOrbReverse } from "../constants/animations";
import SectionLink from "./SectionLink";

function getNavOffsetPx() {
  const styles = getComputedStyle(document.documentElement);
  const navHeight = styles.getPropertyValue("--nav-height").trim() || "4.25rem";
  const rootPx = parseFloat(styles.fontSize) || 16;
  const value = parseFloat(navHeight);
  return navHeight.endsWith("rem") ? value * rootPx : value;
}

// Split once on the first comma so the same, unaltered subtitle copy can
// read as a short headline plus a supporting line.
const commaIndex = SERVICES_SECTION.subtitle.indexOf(", ");
const HEADLINE = SERVICES_SECTION.subtitle.slice(0, commaIndex);
const SUPPORTING_LINE = SERVICES_SECTION.subtitle.slice(commaIndex + 2);

function ServicesIntro({ className = "", showCta = true }) {
  return (
    <div className={`text-center xl:text-left ${className}`}>
      <div className="text-[1.15rem] font-semibold uppercase tracking-[0.2em] text-indigo-600 xl:text-[1.35rem]">
        {SERVICES_SECTION.title}
      </div>

      <h2 className="mt-2 text-[1.575rem] font-extrabold leading-[1.25] tracking-tight text-indigo-950 sm:text-[1.8rem] md:text-[2.25rem] xl:text-[2.475rem] 2xl:text-[2.8125rem]">
        {HEADLINE}
      </h2>

      <p className="mx-auto mt-6 max-w-lg text-[0.72rem] leading-relaxed text-gray-500 sm:text-[0.81rem] xl:mx-0 xl:max-w-xl xl:text-[1.0125rem]">
        {SUPPORTING_LINE}
      </p>

      {showCta && (
        <SectionLink sectionId="contactus" className="service-cta-button">
          Discuss Your Project
          <span aria-hidden="true">↗</span>
        </SectionLink>
      )}
    </div>
  );
}

function ServicesSection() {
  const [asideHeight, setAsideHeight] = useState(undefined);
  const sectionRef = useRef(null);
  const asideRef = useRef(null);
  const pinRef = useRef(null);

  useEffect(() => {
    let frame = 0;

    const sync = () => {
      const section = sectionRef.current;
      const aside = asideRef.current;
      const pin = pinRef.current;

      if (!section) {
        return;
      }

      if (!aside || !pin || window.innerWidth < 1280) {
        aside?.style.removeProperty("--service-intro-sticky-top");
        setAsideHeight(undefined);
        return;
      }

      const pinHeight = pin.offsetHeight;
      const navOffset = getNavOffsetPx();
      const stickyTop = Math.max(
        navOffset + 16,
        (window.innerHeight - pinHeight) / 2,
      );
      aside.style.setProperty("--service-intro-sticky-top", `${stickyTop}px`);

      const stack = section.querySelector(".service-stack");
      const lastCard = stack?.querySelector(".service-stack-card:last-of-type");

      if (lastCard) {
        const marginBottom =
          parseFloat(getComputedStyle(lastCard).marginBottom) || 0;
        const trackHeight =
          lastCard.offsetTop + lastCard.offsetHeight + marginBottom;
        const minTrack = pinHeight + stickyTop + 32;
        setAsideHeight(Math.max(trackHeight, minTrack));
      } else {
        setAsideHeight(undefined);
      }
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      id="services"
      aria-label="Services"
    >
      <div
        className="sticky top-0 -mb-[100vh] h-screen w-full overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-white to-sky-100" />
        <div
          className="services-line-pattern pointer-events-none absolute inset-0 opacity-70"
          style={{
            maskImage: "linear-gradient(180deg, black, transparent 85%)",
          }}
        />
        <div
          className="about-grid-pattern pointer-events-none absolute inset-0 opacity-60"
          style={{
            maskImage:
              "radial-gradient(circle at 20% 15%, black, transparent 55%)",
          }}
        />

        <motion.div
          className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-indigo-200/20"
          animate={floatingOrb.animate}
          transition={floatingOrb.transition}
        />
        <motion.div
          className="pointer-events-none absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-sky-200/15"
          animate={floatingOrbReverse.animate}
          transition={floatingOrbReverse.transition}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1800px] px-4 py-16 sm:px-6 md:py-24 lg:px-10 xl:px-14 2xl:px-20">
        <div className="mb-10 max-w-3xl xl:hidden">
          <ServicesIntro showCta={false} />
        </div>

        <div className="xl:grid xl:grid-cols-[1fr_1.3fr] xl:items-start xl:gap-8 2xl:gap-10">
          <aside
            ref={asideRef}
            className="relative hidden self-start xl:block"
            style={asideHeight ? { height: asideHeight } : undefined}
          >
            <div ref={pinRef} className="service-intro-pin">
              <ServicesIntro />
            </div>
          </aside>

          <div>
            <CardList />
          </div>
        </div>
      </div>
    </section>
  );
}

export default ServicesSection;
